import * as React from 'react';

export const LOAD_MORE_ROOT_MARGIN = '0px 0px 400px 0px';

interface InfiniteScrollSentinelProps {
  /** Called when the sentinel enters the prefetch margin. */
  onVisible: () => void;
  /** When false, the observer is not attached (e.g. no more pages / mid-load). */
  enabled: boolean;
  /** Re-observes the sentinel when newly appended content changes the list. */
  contentVersion: number;
  /** Scroll container the sentinel lives in; defaults to the page viewport. */
  root?: React.RefObject<HTMLElement>;
}

/**
 * Prefetches another page before the user reaches the end of the account list.
 * Re-observing after each append lets local pages continue loading while the
 * sentinel remains inside the prefetch margin.
 */
export const InfiniteScrollSentinel: React.VFC<InfiniteScrollSentinelProps> = ({
  onVisible,
  enabled,
  contentVersion,
  root,
}) => {
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  // Keep the latest callback without re-creating the observer each render.
  const onVisibleRef = React.useRef(onVisible);
  onVisibleRef.current = onVisible;

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!enabled || !node || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onVisibleRef.current();
        }
      },
      {
        root: root?.current ?? null,
        rootMargin: LOAD_MORE_ROOT_MARGIN,
      },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [contentVersion, enabled, root]);

  return (
    <div ref={sentinelRef} data-testid="account-infinite-scroll-sentinel" />
  );
};
